import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useSettings } from "../../lib/settings";

export default function PartnerProjekte({ compact = false }) {
  const settings = useSettings();
  const partners = settings?.partners || [];
  if (!partners.length) return null;

  return (
    <section className="py-24 border-t border-white/5" data-testid="partner-projekte-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">SAT Partnerwelt</div>
          <h2 className="serif text-3xl md:text-5xl font-light tracking-tight leading-tight">
            Unsere<br/><span className="italic" style={{ color: "#D4AF37" }}>Partnerprojekte.</span>
          </h2>
          <p className="text-white/55 text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Die SAT Akademie ist Teil eines größeren Ökosystems von Projekten und Bewegungen, die gemeinsam Menschen begleiten, ihr volles Potenzial zu entfalten.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {partners.map((p) => <PartnerCard key={p.key} p={p} />)}
        </div>

        {!compact && settings?.future_projects?.length > 0 && (
          <div className="mt-20 text-center">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-6">Bald · in Vorbereitung</div>
            <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto" data-testid="future-projects">
              {settings.future_projects.map((fp) => (
                <span key={fp.key}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border border-white/10 text-white/60 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition">
                  <Sparkles size={10} /> {fp.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const PartnerCard = ({ p }) => {
  const external = p.url && (p.url.startsWith("http://") || p.url.startsWith("https://"));
  const Wrapper = external ? "a" : Link;
  const wrapperProps = external
    ? { href: p.url || "#", target: "_blank", rel: "noopener noreferrer" }
    : { to: p.url || "#" };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`partner-card-${p.key}`}
      className="group relative block p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#D4AF37]/40 transition-all duration-500 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
           style={{ background: `radial-gradient(circle at 30% 20%, ${p.color}30 0%, transparent 60%)` }} />

      <div className="relative">
        <div className="h-20 mb-6 flex items-center">
          {p.logo ? (
            <img src={p.logo} alt={p.name} className="max-h-full max-w-[140px] object-contain" />
          ) : (
            <div className="serif text-3xl tracking-tight" style={{ color: p.color }}>{p.name}</div>
          )}
        </div>
        <h3 className="serif text-2xl mb-3" style={{ color: p.color }}>{p.name}</h3>
        <p className="text-white/65 text-sm leading-relaxed mb-6 min-h-[80px]">{p.description}</p>
        <div className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all"
             style={{ color: p.color }}>
          {p.cta} <ArrowRight size={14} />
        </div>
      </div>
    </Wrapper>
  );
};
